<template>
  <Teleport to="body">
    <div class="exclusion-form-backdrop fixed inset-0 z-[3200] flex items-center justify-end bg-slate-950/15 p-4 md:p-8"
      @click.self="emit('close')">
      <section
        class="exclusion-form-panel max-h-[calc(100vh-2rem)] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-6 text-slate-950 shadow-2xl"
        role="dialog" aria-modal="true" aria-labelledby="exclusion-form-title">
        <header class="flex items-center justify-between gap-4">
          <h2 id="exclusion-form-title" class="m-0 text-lg font-extrabold tracking-normal text-slate-950">
            {{ formTitle }}
          </h2>
          <button type="button"
            class="flex h-9 w-9 items-center justify-center rounded-md bg-transparent p-0 text-slate-700 hover:bg-slate-100 hover:text-violet-700"
            aria-label="Close" @click="emit('close')">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div class="mt-5">
          <label for="exclusion-group-name" class="mb-2 block text-xs font-extrabold text-slate-950">
            Group Name
          </label>
          <div class="relative">
            <input id="exclusion-group-name" v-model.trim="draft.name" type="text" maxlength="50"
              class="h-10 w-full rounded-md border border-slate-300 bg-white px-3 pr-14 text-sm text-slate-950 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="store.saving || draft.isSystem" />
            <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
              {{ draft.name.length }}/50
            </span>
          </div>
        </div>

        <div class="mt-5">
          <label for="exclusion-group-description" class="mb-2 block text-xs font-extrabold text-slate-950">
            Description (optional)
          </label>
          <div class="relative">
            <textarea id="exclusion-group-description" v-model.trim="draft.description" maxlength="100"
              class="min-h-12 w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 pr-16 text-sm text-slate-950 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="store.saving"></textarea>
            <span class="absolute right-3 top-3 text-xs text-slate-500">
              {{ draft.description.length }}/100
            </span>
          </div>
        </div>

        <div class="mt-5 flex items-center justify-between gap-4">
          <div>
            <strong class="block text-xs font-extrabold text-slate-950">Exclude Group</strong>
            <p class="m-0 mt-1 text-xs text-slate-500">
              Select if you want to exclude symbols from this group.
            </p>
          </div>
          <label class="relative inline-flex h-6 w-11 shrink-0 items-center">
            <input v-model="draft.isExcluded" type="checkbox" class="peer sr-only" :disabled="store.saving" />
            <span
              class="absolute inset-0 rounded-full bg-slate-300 transition peer-checked:bg-violet-700 peer-disabled:cursor-not-allowed peer-disabled:opacity-60"></span>
            <span
              class="absolute left-1 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5"></span>
          </label>
        </div>

        <section class="mt-6">
          <div class="mb-2 flex items-center justify-between gap-4">
            <h3 class="m-0 text-xs font-extrabold text-slate-950">Symbols ({{ draft.symbols.length }})</h3>
            <button type="button"
              class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 font-bold text-violet-700 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="store.saving || !canAddSymbol" @click="addSymbol">
              <i class="fa-solid fa-plus"></i>
              Add Symbol
            </button>
          </div>

          <div class="relative">
            <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input v-model.trim="symbolQuery" type="text" list="available-exclusion-symbols"
              placeholder="Search symbol to add..."
              class="h-10 w-full rounded-t-md border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-950 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="store.saving" @keydown.enter.prevent="addSymbol" />
            <datalist id="available-exclusion-symbols">
              <option v-for="coin in symbolSuggestions" :key="coin.symbol" :value="coin.symbol">
                {{ coin.coinName }}
              </option>
            </datalist>
          </div>

          <div class="max-h-64 overflow-y-auto rounded-b-md border border-t-0 border-slate-200">
            <div v-for="symbol in draft.symbols" :key="symbol.symbol"
              class="grid min-h-12 grid-cols-[30px_70px_1fr_36px] items-center gap-2 border-t border-slate-200 px-3 first:border-t-0">
              <span
                class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-950 text-xs font-extrabold text-white">
                {{ symbol.symbol.slice(0, 1) }}
              </span>
              <strong class="text-sm text-slate-950">{{ symbol.symbol }}</strong>
              <span class="truncate text-xs text-slate-500">{{ symbol.coinName || symbol.symbol }}</span>
              <button type="button"
                class="flex h-8 w-8 items-center justify-center rounded-md bg-transparent p-0 text-slate-600 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="store.saving" @click="removeSymbol(symbol)">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div v-if="draft.symbols.length === 0" class="px-3 py-8 text-center text-sm text-slate-500">
              No symbols in this group.
            </div>
          </div>
        </section>

        <footer class="mt-7 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <button v-if="canDelete" type="button"
            class="h-11 rounded-md border border-red-500 bg-white px-5 font-extrabold text-red-600 hover:bg-red-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="store.saving" @click="deleteGroup">
            Delete Group
          </button>
          <span v-else></span>

          <div class="flex gap-3">
            <button type="button"
              class="h-11 min-w-24 rounded-md border border-slate-300 bg-white px-5 font-extrabold text-slate-950 hover:bg-slate-50"
              @click="emit('close')">
              Cancel
            </button>
            <button type="button"
              class="h-11 min-w-32 rounded-md bg-violet-700 px-5 font-extrabold text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="store.saving || !draft.name" @click="saveGroup">
              Save
            </button>
          </div>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, getCurrentInstance, reactive, ref, watch } from 'vue';
import { useExclusionGroupsStore } from '@/stores/exclusionGroups';

const props = defineProps({
  group: {
    type: Object,
    default: null,
  },
  availableSymbols: {
    type: Array,
    default: () => [],
  },
  blockedSymbols: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'saved']);
const store = useExclusionGroupsStore();
const { proxy } = getCurrentInstance();
const symbolQuery = ref('');

const draft = reactive({
  id: null,
  name: '',
  description: '',
  groupType: null,
  isExcluded: false,
  isSystem: false,
  symbols: [],
});

const isCreateMode = computed(() => !draft.id);
const isBlockedGroup = computed(() => (
  draft.groupType === 'BLOCKED' ||
  draft.groupType === 'BLOCK_CUSTOM' ||
  draft.name.toUpperCase() === 'BLOCKED'
));
const canDelete = computed(() => draft.id && !draft.isSystem && !isBlockedGroup.value);
const formTitle = computed(() => {
  if (!draft.id) return 'Add Custom Group';
  if (draft.isSystem) return 'Edit System Group';
  return 'Edit Group';
});

const normalizedAvailableSymbols = computed(() => {
  const seen = new Set();
  return props.availableSymbols
    .map(coin => {
      const symbol = String(coin.coinName || coin.symbol || '').trim().toUpperCase();
      return {
        symbol,
        coinName: coin.coinNameCmc || coin.name || coin.coinName || symbol,
      };
    })
    .filter(coin => {
      if (!coin.symbol || seen.has(coin.symbol)) return false;
      seen.add(coin.symbol);
      return true;
    });
});

const symbolSuggestions = computed(() => {
  const query = symbolQuery.value.toUpperCase();
  const selected = new Set(draft.symbols.map(item => item.symbol));
  return normalizedAvailableSymbols.value
    .filter(coin => !selected.has(coin.symbol))
    .filter(coin => !query || coin.symbol.includes(query) || String(coin.coinName).toUpperCase().includes(query))
    .slice(0, 20);
});

const canAddSymbol = computed(() => symbolQuery.value.trim().length > 0);
const blockedSymbolSet = computed(() => new Set(
  props.blockedSymbols.map(symbol => String(symbol).trim().toUpperCase()).filter(Boolean)
));

function formatGroupName(name) {
  if (!name) return '-';
  const clean = String(name).trim().toUpperCase();
  if (clean === 'BSTOCKS' || clean === 'TRADFI_BSTOCKS') return 'TradFi_bStocks';
  if (clean === 'STOCK' || clean === 'TRADFI_FUTURES') return 'TradFi_Futures';
  if (clean === 'TCOMMODITIES' || clean === 'TRADFI_TCOMMODITIES') return 'TradFi_tCommodities';
  return name;
}

watch(
  () => props.group,
  group => {
    draft.id = group?.id || null;
    draft.name = formatGroupName(group?.name || '');
    draft.description = group?.description || '';
    draft.groupType = group?.groupType || group?.type || group?.group_type || null;
    draft.isExcluded = Boolean(group?.isExcluded);
    draft.isSystem = Boolean(group?.isSystem);
    draft.symbols = (group?.symbols || []).map(item => ({
      symbol: String(item.symbol || '').toUpperCase(),
      coinName: item.coinName || item.symbol,
    }));
    symbolQuery.value = '';
  },
  { immediate: true }
);

function addSymbol() {
  if (store.saving) return;

  const symbol = symbolQuery.value.trim().toUpperCase();
  if (!symbol || draft.symbols.some(item => item.symbol === symbol)) return;

  const matched = normalizedAvailableSymbols.value.find(item => item.symbol === symbol);
  
  if (blockedSymbolSet.value.has(symbol)) {
    notify('error', `${symbol} is already in the BLOCKED group.`);
    return;
  }

  if (!matched) {
    notify('error', `Invalid symbol: ${symbol}`);
    return;
  }

  

  draft.symbols.push({
    symbol,
    coinName: matched.coinName || symbol,
  });

  symbolQuery.value = '';
}

function removeSymbol(symbol) {
  if (store.saving) return;

  draft.symbols = draft.symbols.filter(item => item.symbol !== symbol.symbol);
}

async function saveGroup() {
  const payload = {
    name: draft.isSystem ? props.group?.name : draft.name,
    description: draft.description,
    isExcluded: draft.isExcluded,
    symbols: draft.symbols.map(item => item.symbol),
  };

  try {
    if (isCreateMode.value) {
      await store.createGroup({
        ...payload,
        groupType: 'ANORMAL_CUSTOM',
      });
    } else {
      await store.updateGroup(draft.id, payload);
    }

    notify('success', isCreateMode.value ? 'Exclusion group created.' : 'Exclusion group updated.');
    emit('saved');
    emit('close');
  } catch (error) {
    console.error('Error saving exclusion group:', error);
    const errMsg = error?.response?.data?.message || error?.message || 'Unable to save exclusion group.';
    notify('error', errMsg);
  }
}

async function deleteGroup() {
  if (!canDelete.value) return;
  try {
    await store.deleteGroup(draft.id, {
      name: draft.name,
      description: draft.description,
      isExcluded: draft.isExcluded,
      symbols: draft.symbols.map(item => item.symbol),
    });
    notify('success', 'Exclusion group deleted.');
    emit('saved');
    emit('close');
  } catch (error) {
    console.error('Error deleting exclusion group:', error);
    notify('error', 'Unable to delete exclusion group.');
  }
}

function notify(type, message) {
  proxy?.$toast?.open({
    message,
    type,
    position: 'top-right',
  });
}
</script>

<style>
body.dark-mode .exclusion-form-backdrop {
  background: rgba(15, 23, 42, 0.72);
}

body.dark-mode .exclusion-form-panel {
  background: #000000;
  border: 1px solid #363c4e;
  color: #ffffff;
}

body.dark-mode .exclusion-form-panel h2,
body.dark-mode .exclusion-form-panel h3,
body.dark-mode .exclusion-form-panel label,
body.dark-mode .exclusion-form-panel strong,
body.dark-mode .exclusion-form-panel .text-slate-950 {
  color: #ffffff;
}

body.dark-mode .exclusion-form-panel .text-slate-400,
body.dark-mode .exclusion-form-panel .text-slate-500,
body.dark-mode .exclusion-form-panel .text-slate-600,
body.dark-mode .exclusion-form-panel .text-slate-700 {
  color: #cbd5e1;
}

body.dark-mode .exclusion-form-panel input,
body.dark-mode .exclusion-form-panel textarea {
  background: #2b3139;
  border-color: #475569;
  color: #f8fafc;
}

body.dark-mode .exclusion-form-panel input::placeholder {
  color: #94a3b8;
}

body.dark-mode .exclusion-form-panel textarea::placeholder {
  color: #94a3b8;
}

body.dark-mode .exclusion-form-panel button {
  border-color: #475569;
}

body.dark-mode .exclusion-form-panel button.bg-white {
  background: #2b3139;
  color: #eaecef;
}

body.dark-mode .exclusion-form-panel button.bg-white:hover {
  background: #363c4e;
}

body.dark-mode .exclusion-form-panel button.bg-violet-700 {
  background: #6d28d9;
  border-color: #6d28d9;
  color: #ffffff;
}

body.dark-mode .exclusion-form-panel button.bg-violet-700:hover {
  background: #5b21b6;
}

body.dark-mode .exclusion-form-panel button.border-red-500 {
  border-color: #ef4444;
  color: #f87171;
}

body.dark-mode .exclusion-form-panel button.border-red-500:hover {
  background: #dc2626;
  color: #ffffff;
}

body.dark-mode .exclusion-form-panel .border-slate-200 {
  border-color: #363c4e;
}

body.dark-mode .exclusion-form-panel .bg-slate-950 {
  background: #475569;
}
</style>
